/**
 * Raw Material Transport Service API Client
 * Handles all API calls for route management, execution, and scheduling
 *
 * Service URLs come from serviceConfig.ts (single source of truth).
 * To change a service URL/port, update ONLY the .env file.
 */

import { getServiceUrl } from '../../config/serviceConfig';

const API_BASE_URL = getServiceUrl('transport');
const MANUFACTURING_API_BASE_URL = getServiceUrl('manufacturing');

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface RouteStop {
  id?: number;
  gaushalaId: number;
  gaushalaName: string;
  gaushalaAddress?: string;
  gaushalaLatitude?: number;
  gaushalaLongitude?: number;
  sequenceOrder: number;
  expectedQuantityMinKg?: number;
  expectedQuantityMaxKg?: number;
  estimatedArrivalTime?: string;
  rawMaterialTypeId?: number;
  rawMaterialTypeName?: string;
  isActive?: boolean;
}

export interface RawMaterialRoute {
  id?: number;
  routeCode: string;
  routeName: string;
  transportUnitId?: number;
  manufacturingUnitId: number;
  mfgUnitName: string;
  mfgUnitAddress?: string;
  mfgUnitLatitude?: number;
  mfgUnitLongitude?: number;
  scheduleTypeId?: number;
  scheduleType?: string;
  scheduleConfig?: Record<string, any>;
  routeStartTime?: string;
  scheduleTypeName?: string;
  status?: string;
  statusCode?: string;
  statusName?: string;
  assignedDriverId?: number;
  assignedDriverName?: string;
  assignedVehicleId?: number;
  assignedVehicleNumber?: string;
  totalDistanceKm?: number;
  estimatedDurationMin?: number;
  totalStops?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  stops?: RouteStop[];
}

export interface RouteExecution {
  id?: number;
  routeId: number;
  routeCode?: string;
  routeName?: string;
  executionDate: string;
  status?: string;
  statusCode?: string;
  statusName?: string;
  driverId?: number;
  driverName?: string;
  vehicleId?: number;
  vehicleNumber?: string;
  totalQuantityCollectedKg?: number;
  totalStopsCompleted?: number;
  totalStopsSkipped?: number;
  actualDistanceKm?: number;
  actualDurationMin?: number;
  startedAt?: string;
  completedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface StopLog {
  id?: number;
  executionId: number;
  stopId: number;
  gaushalaId: number;
  gaushalaName?: string;
  gaushalaLatitude?: number;
  gaushalaLongitude?: number;
  sequenceOrder?: number;
  status?: string;
  statusCode?: string;  // Backend returns statusCode
  statusName?: string;  // Backend returns statusName
  quantityCollectedKg?: number;
  rawMaterialTypeId?: number;
  dungTypeId?: number;
  collectionPhotoUrl?: string;
  skipReason?: string;
  notes?: string;
  arrivedAt?: string;
  departedAt?: string;
  createdAt?: string;
  partitionId?: number;
  partitionNumber?: number;
  partitionLabel?: string;
}

export interface CreateRouteRequest {
  routeCode: string;
  routeName: string;
  manufacturingUnitId: number;
  mfgUnitName: string;
  mfgUnitAddress: string;
  mfgUnitLatitude?: number;
  mfgUnitLongitude?: number;
  scheduleTypeId: number;
  scheduleConfig?: string;
}

export interface AddStopRequest {
  gaushalaId: number;
  gaushalaName: string;
  gaushalaAddress?: string;
  gaushalaLatitude?: number;
  gaushalaLongitude?: number;
  sequenceOrder: number;
  expectedQuantityMinKg?: number;
  expectedQuantityMaxKg?: number;
  rawMaterialTypeId?: number;
}

export interface CompleteStopRequest {
  quantityCollectedKg: number;
  rawMaterialTypeId?: number;
  dungTypeId?: number;
  collectionPhotoUrl?: string;
}

export interface SkipStopRequest {
  skipReason: string;
  notes?: string;
}

export interface CompleteDeliveryRequest {
  actualDistanceKm: number;
  actualDurationMin: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Lookup DTOs for dropdowns
export interface ManufacturingUnitDTO {
  id: number;
  name: string;
  code: string;
  address?: string;
  latitude?: number;
  longitude?: number;
}

export interface GaushalaDTO {
  id: number;
  gaushalaName: string;
  registeredAddress?: string;
  latitude?: number;
  longitude?: number;
  contactNumber?: string;
  headName?: string;
  status?: string;
}

export interface ScheduleTypeDTO {
  id: number;
  code: string;
  name: string;
  description?: string;
}

export interface RawMaterialTypeDTO {
  id: number;
  code: string;
  name: string;
  isActive?: boolean;
}

export interface DriverDTO {
  id: number;
  name: string;
  phoneNumber?: string;
  employeeCode?: string;
  statusName?: string;
  isAvailable?: boolean;
  vehicleId?: number;
  vehicleRegistration?: string;
  licenseNumber?: string;
  rating?: number;
  totalTrips?: number;
  partitionCount?: number;
}

export interface VehicleDTO {
  id: number;
  registrationNumber: string;
  type?: string;
  fuelType?: string;
  make?: string;
  model?: string;
  capacity?: number;
  capacityKg?: number;
  status?: string;
  statusName?: string;
  isActive?: boolean;
  yearOfManufacture?: number;
  partitionCount?: number;
}

export interface GaushalaPickupRoute {
  routeId: number;
  routeCode: string;
  routeName: string;
  gaushalaName: string;
  gaushalaAddress?: string;
  expectedQuantityMinKg?: number;
  expectedQuantityMaxKg?: number;
  pickupTime?: string;
  sequenceOrder?: number;
  routeStatusCode: string;
  routeStatusName: string;
  assignedDriverName?: string;
  assignedVehicleNumber?: string;
  scheduleTypeName?: string;
  scheduleDate?: string;  // Date from scheduleConfig for Weekly/Custom/Manual schedules

  // ===== Execution/Driver Status Fields (for real-time tracking) =====
  /** Current execution ID for today (null if no execution exists) */
  executionId?: number;
  /** Current execution status: SCHEDULED, STARTED, IN_PROGRESS, COMPLETED */
  executionStatus?: string;
  /** This gaushala's stop status in today's execution: PENDING, ARRIVED, COMPLETED, SKIPPED */
  currentStopStatus?: string;
  /** When driver arrived at this gaushala (null if not arrived yet) */
  driverArrivedAt?: string;
  /** When driver departed from this gaushala (null if not departed yet) */
  driverDepartedAt?: string;
  /** Which stop the driver is currently at (1-based index) */
  currentStopIndex?: number;
  /** Total number of stops in this route */
  totalStops?: number;
  /** Quantity collected from this gaushala (null if not completed) */
  quantityCollectedKg?: number;
  /** Human-readable progress message: "Driver en route", "Driver arrived", "Completed" */
  driverStatusMessage?: string;
  /** Partition label assigned during collection (e.g., "P1", "RK-2") */
  partitionLabel?: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    if (response.status === 401) {
      console.error('[Transport API] 401 Unauthorized - session may have expired');
      throw new Error('Session expired. Please login again.');
    }
    const body = await response.json().catch(() => ({ message: response.statusText }));
    const message = body?.error?.message || body?.message || `HTTP ${response.status}`;
    throw new Error(message);
  }
  return response.json();
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('saubhagya_jwt_token');
  if (!token) {
    console.warn('[Transport API] No JWT token found in localStorage');
  }
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
}

/**
 * Normalize backend route response: maps statusCode → status
 * Backend DTOs return statusCode/statusName; frontend reads status.
 */
function normalizeRoute(route: RawMaterialRoute): RawMaterialRoute {
  return {
    ...route,
    status: route.status || route.statusCode,
  };
}

// ============================================================================
// ROUTE MANAGEMENT APIs
// ============================================================================

export const transportApi = {
  // Create Route
  createRoute: async (request: CreateRouteRequest): Promise<RawMaterialRoute> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/raw-material-routes`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request)
    });
    const route = await handleResponse<RawMaterialRoute>(response);
    return normalizeRoute(route);
  },

  // List Routes (with pagination)
  listRoutes: async (page: number = 0, size: number = 10): Promise<{ content: RawMaterialRoute[], totalElements: number }> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/raw-material-routes?page=${page}&size=${size}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const data = await handleResponse<RawMaterialRoute[] | { content: RawMaterialRoute[], totalElements: number }>(response);

    // Backend returns a plain array, normalize to page structure
    if (Array.isArray(data)) {
      return { content: data.map(normalizeRoute), totalElements: data.length };
    }
    return { ...data, content: data.content.map(normalizeRoute) };
  },

  // Get Route Details
  getRouteDetails: async (routeId: number): Promise<RawMaterialRoute> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/raw-material-routes/${routeId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const route = await handleResponse<RawMaterialRoute>(response);
    return normalizeRoute(route);
  },

  // Update Route
  updateRoute: async (routeId: number, request: Partial<RawMaterialRoute>): Promise<RawMaterialRoute> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/raw-material-routes/${routeId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(request)
    });
    const route = await handleResponse<RawMaterialRoute>(response);
    return normalizeRoute(route);
  },

  // Assign Driver
  assignDriver: async (routeId: number, driverId: number): Promise<RawMaterialRoute> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/raw-material-routes/${routeId}/assign-driver?driverId=${driverId}`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    const route = await handleResponse<RawMaterialRoute>(response);
    return normalizeRoute(route);
  },

  // Assign Vehicle
  assignVehicle: async (routeId: number, vehicleId: number): Promise<RawMaterialRoute> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/raw-material-routes/${routeId}/assign-vehicle?vehicleId=${vehicleId}`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    const route = await handleResponse<RawMaterialRoute>(response);
    return normalizeRoute(route);
  },

  // Activate Route
  activateRoute: async (routeId: number): Promise<RawMaterialRoute> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/raw-material-routes/${routeId}/activate`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    const route = await handleResponse<RawMaterialRoute>(response);
    return normalizeRoute(route);
  },

  // Pause Route
  pauseRoute: async (routeId: number): Promise<RawMaterialRoute> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/raw-material-routes/${routeId}/pause`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    const route = await handleResponse<RawMaterialRoute>(response);
    return normalizeRoute(route);
  },

  // Resume Route
  resumeRoute: async (routeId: number): Promise<RawMaterialRoute> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/raw-material-routes/${routeId}/resume`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    const route = await handleResponse<RawMaterialRoute>(response);
    return normalizeRoute(route);
  },

  // Cancel Route
  cancelRoute: async (routeId: number): Promise<RawMaterialRoute> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/raw-material-routes/${routeId}/cancel`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    const route = await handleResponse<RawMaterialRoute>(response);
    return normalizeRoute(route);
  },

  // Add Stop
  addStop: async (routeId: number, request: AddStopRequest): Promise<RouteStop> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/raw-material-routes/${routeId}/stops`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request)
    });
    return handleResponse<RouteStop>(response);
  },

  // Remove Stop
  removeStop: async (routeId: number, stopId: number): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/raw-material-routes/${routeId}/stops/${stopId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
  },

  // ============================================================================
  // EXECUTION (Driver Workflow) APIs
  // ============================================================================

  /**
   * Get all executions (past, today, and future).
   * @param includeAll If true, includes COMPLETED and CANCELLED executions (for history view)
   */
  getExecutions: async (includeAll: boolean = true): Promise<RouteExecution[]> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/executions?includeAll=${includeAll}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const result = await handleResponse<{ data: RouteExecution[] }>(response);
    return result.data || [];
  },

  /**
   * Create executions for upcoming days (pre-create future executions).
   * @param daysAhead Number of days ahead (default 7, max 7)
   */
  createUpcomingExecutions: async (daysAhead: number = 7): Promise<{ executionsCreated: number }> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/executions/create-upcoming?daysAhead=${daysAhead}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse<{ executionsCreated: number }>(response);
  },

  /**
   * Create executions for past days (backfill missed executions).
   * @param daysBack Number of days back (default 7, max 30)
   */
  createPastExecutions: async (daysBack: number = 7): Promise<{ executionsCreated: number }> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/executions/create-past?daysBack=${daysBack}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse<{ executionsCreated: number }>(response);
  },

  // Get Execution Details
  getExecution: async (executionId: number): Promise<RouteExecution> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/executions/${executionId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse<RouteExecution>(response);
  },

  /**
   * Get today's execution for a specific route
   * Filters all executions by routeId and today's date
   */
  getTodayExecutionByRouteId: async (routeId: number): Promise<RouteExecution | null> => {
    try {
      const executions = await transportApi.getExecutions(true);
      const today = new Date().toISOString().split('T')[0];

      const todayExecution = executions.find(exec =>
        exec.routeId === routeId &&
        exec.executionDate?.split('T')[0] === today
      );

      return todayExecution || null;
    } catch (error) {
      console.error('Failed to get today execution for route:', error);
      return null;
    }
  },

  // Start Execution
  startExecution: async (executionId: number): Promise<RouteExecution> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/executions/${executionId}/start`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse<RouteExecution>(response);
  },

  // Mark Stop Arrived
  markStopArrived: async (executionId: number, stopId: number): Promise<StopLog> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/executions/${executionId}/stops/${stopId}/arrive`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({})
    });
    return handleResponse<StopLog>(response);
  },

  // Complete Stop
  completeStop: async (executionId: number, stopId: number, request: CompleteStopRequest): Promise<StopLog> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/executions/${executionId}/stops/${stopId}/complete`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(request)
    });
    return handleResponse<StopLog>(response);
  },

  // Skip Stop
  skipStop: async (executionId: number, stopId: number, request: SkipStopRequest): Promise<StopLog> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/executions/${executionId}/stops/${stopId}/skip`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(request)
    });
    return handleResponse<StopLog>(response);
  },

  // Complete Delivery
  completeDelivery: async (executionId: number, request: CompleteDeliveryRequest): Promise<RouteExecution> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/executions/${executionId}/complete`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(request)
    });
    return handleResponse<RouteExecution>(response);
  },

  // Get Execution Stops
  getExecutionStops: async (executionId: number): Promise<StopLog[]> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/executions/${executionId}/stops`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const result = await handleResponse<{ data: StopLog[] }>(response);
    return result.data || [];
  },

  // Validate if route can be ended (all stops completed or skipped)
  validateCanEndRoute: async (executionId: number): Promise<{
    canEndRoute: boolean;
    totalStops: number;
    completedStops: number;
    skippedStops: number;
    pendingStops: number;
    arrivedStops: number;
    incompleteStops: Array<{ stopLogId: number; gaushalaName: string; status: string }>;
    message: string;
  }> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/executions/${executionId}/can-end-route`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse<any>(response);
  },

  // ============================================================================
  // MANUFACTURING VIEW APIs
  // These endpoints call the Manufacturing Service (not Transport Service)
  // to avoid transport service's TenantInterceptor blocking MFG_OPERATOR role
  // ============================================================================

  // Get Scheduled Routes for Manufacturing Unit (calls manufacturing-service)
  getScheduledRoutes: async (manufacturingUnitId: number): Promise<RawMaterialRoute[]> => {
    const response = await fetch(`${MANUFACTURING_API_BASE_URL}/api/v1/manufacturing/${manufacturingUnitId}/routes/scheduled`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const result = await handleResponse<any>(response);
    // Manufacturing service wraps response in ApiResponse { success, data }
    const routes = result.data || result;
    if (Array.isArray(routes)) return routes.map(normalizeRoute);
    return (routes.content || []).map(normalizeRoute);
  },

  // Get Active Routes for Manufacturing Unit (calls manufacturing-service)
  getActiveRoutes: async (manufacturingUnitId: number): Promise<RawMaterialRoute[]> => {
    const response = await fetch(`${MANUFACTURING_API_BASE_URL}/api/v1/manufacturing/${manufacturingUnitId}/routes/active`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const result = await handleResponse<any>(response);
    const routes = result.data || result;
    if (Array.isArray(routes)) return routes.map(normalizeRoute);
    return (routes.content || []).map(normalizeRoute);
  },

  // Get Route Statistics for Manufacturing Unit (calls manufacturing-service)
  getRouteStatistics: async (manufacturingUnitId: number): Promise<Record<string, number>> => {
    const response = await fetch(`${MANUFACTURING_API_BASE_URL}/api/v1/manufacturing/${manufacturingUnitId}/routes/statistics`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const result = await handleResponse<any>(response);
    return result.data || result || {};
  },

  // Get Incoming Deliveries for Manufacturing Unit (alias for getActiveRoutes)
  getIncomingDeliveries: async (manufacturingUnitId: number): Promise<RawMaterialRoute[]> => {
    const response = await fetch(`${MANUFACTURING_API_BASE_URL}/api/v1/manufacturing/${manufacturingUnitId}/routes/active`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const result = await handleResponse<any>(response);
    const routes = result.data || result;
    if (Array.isArray(routes)) return routes.map(normalizeRoute);
    return (routes.content || []).map(normalizeRoute);
  },

  // ============================================================================
  // LOOKUP APIs (for dropdowns)
  // ============================================================================

  // Get all manufacturing units for dropdown
  getManufacturingUnits: async (search?: string): Promise<ManufacturingUnitDTO[]> => {
    const url = search
      ? `${API_BASE_URL}/api/v1/lookup/manufacturing-units?search=${encodeURIComponent(search)}`
      : `${API_BASE_URL}/api/v1/lookup/manufacturing-units`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const result = await handleResponse<ApiResponse<ManufacturingUnitDTO[]>>(response);
    return result.data || [];
  },

  // Get all gaushalas for dropdown
  getGaushalas: async (search?: string): Promise<GaushalaDTO[]> => {
    const url = search
      ? `${API_BASE_URL}/api/v1/lookup/gaushalas?search=${encodeURIComponent(search)}`
      : `${API_BASE_URL}/api/v1/lookup/gaushalas`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const result = await handleResponse<ApiResponse<GaushalaDTO[]>>(response);
    return result.data || [];
  },

  // Get all schedule types for dropdown
  getScheduleTypes: async (): Promise<ScheduleTypeDTO[]> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/lookup/schedule-types`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const result = await handleResponse<ApiResponse<ScheduleTypeDTO[]>>(response);
    return result.data || [];
  },

  // Get all raw material types for dropdown
  getRawMaterialTypes: async (): Promise<RawMaterialTypeDTO[]> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/lookup/raw-material-types`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const result = await handleResponse<ApiResponse<RawMaterialTypeDTO[]>>(response);
    return result.data || [];
  },

  // Get all drivers for the current transport unit
  getDrivers: async (): Promise<DriverDTO[]> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/drivers`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const result = await handleResponse<ApiResponse<DriverDTO[]>>(response);
    return result.data || [];
  },

  // Get all vehicles for the current transport unit
  getVehicles: async (): Promise<VehicleDTO[]> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/vehicles`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const result = await handleResponse<ApiResponse<VehicleDTO[]>>(response);
    return result.data || [];
  },

  // Get pickup routes for a gaushala
  getGaushalaPickupRoutes: async (gaushalaId: number): Promise<GaushalaPickupRoute[]> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/gaushalas/${gaushalaId}/pickup-routes`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse<GaushalaPickupRoute[]>(response);
  },

  // Get routes assigned to current driver
  getDriverRoutes: async (): Promise<any[]> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/drivers/me/routes`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const result = await handleResponse<{ data: any[] }>(response);
    return result.data || [];
  },

  // ============================================================================
  // VEHICLE PARTITION MANAGEMENT
  // ============================================================================

  // Get all partitions for a vehicle
  getVehiclePartitions: async (vehicleId: number): Promise<any[]> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/vehicle-partitions?vehicleId=${vehicleId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const result = await handleResponse<ApiResponse<any[]>>(response);
    return result.data || [];
  },

  // Create a new vehicle partition
  createVehiclePartition: async (payload: any): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/vehicle-partitions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    const result = await handleResponse<ApiResponse<any>>(response);
    return result.data || result;
  },

  // Update a vehicle partition
  updateVehiclePartition: async (id: number, payload: any): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/vehicle-partitions/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    const result = await handleResponse<ApiResponse<any>>(response);
    return result.data || result;
  },

  // Delete a vehicle partition
  deleteVehiclePartition: async (id: number): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/vehicle-partitions/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    await handleResponse<void>(response);
  }
};

export default transportApi;
